
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  listMarketingCampaigns,
  listMarketingTemplates,
  createMarketingCampaign,
  getMarketingCampaign,
  updateMarketingCampaign,
  deleteMarketingCampaign,
  archiveMarketingCampaign,
  restoreMarketingCampaign,
  previewCampaignRecipients,
  sendCampaignNow,
  cancelCampaign,
} from "../../api/marketingCampaigns";
import { listServices, listCategories } from "../../api/services";
import { listStaff } from "../../api/staffs";
import { getPortalSettings } from "../../api/clientPortalAdmin";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import ConfirmModal from "../../components/modals/ConfirmModal";
import InfoTooltip from "../../components/common/InfoTooltip";
import ModalCloseX from "../../components/common/ModalCloseX";
import {
  formatDateTimeInTimeZone,
  parseLocalDateTimeInTimeZone,
} from "../../utils/datetime";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import {
  CAMPAIGN_GOALS,
  CAMPAIGN_HELP_SECTIONS,
  CAMPAIGN_TOOLTIPS,
} from "../../constants/marketingCampaigns";
import SafeHtml from "../../components/common/SafeHtml";
import "../MarketingSectionCompact.css";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const pct = (value) => `${Math.round((Number(value || 0) || 0) * 100)}%`;

const CAMPAIGN_STATUS_FILTERS = [
  { value: "all", label: "All active" },
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "sending", label: "Sending" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "archived", label: "Archived" },
];

const CAMPAIGN_DATE_FILTERS = [
  { value: "all_time", label: "All time" },
  { value: "last_30", label: "Last 30 days" },
  { value: "last_90", label: "Last 90 days" },
];

const CAMPAIGN_WIZARD_STEPS = ["Goal", "Template", "Audience", "Preview", "Send"];

const CAMPAIGN_STATUS_GUIDE = [
  {
    status: "Draft",
    description:
      "Saved but not active. Continue it to finish setup, then send now or schedule.",
  },
  {
    status: "Scheduled",
    description:
      "Ready to send at the chosen future time. You can cancel the schedule before it sends.",
  },
  {
    status: "Sending",
    description:
      "The campaign is currently being sent. This usually only appears briefly.",
  },
  {
    status: "Completed",
    description: "The send finished and performance tracking is available.",
  },
  {
    status: "Cancelled",
    description: "A scheduled campaign was stopped before any scheduled send.",
  },
  {
    status: "Archived",
    description:
      "Hidden from the active list, but still kept for history and reporting.",
  },
];

const MONTHS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const buildDefaultFilters = () => ({
  recency: { mode: "within", days: 30 },
  frequency: { days: 90, min: 3 },
  service_ids: [],
  service_category_ids: [],
  staff_ids: [],
  lifetime_spend_min: "",
  recent_spend_days: 60,
  recent_spend_min: "",
  birthday_months: [],
});

const normalizeAudienceFilters = (filters) => {
  const defaults = buildDefaultFilters();
  return {
    ...defaults,
    ...(filters || {}),
    recency: {
      ...defaults.recency,
      ...(filters?.recency || {}),
    },
    frequency: {
      ...defaults.frequency,
      ...(filters?.frequency || {}),
    },
    service_ids: Array.isArray(filters?.service_ids) ? filters.service_ids : [],
    service_category_ids: Array.isArray(filters?.service_category_ids)
      ? filters.service_category_ids
      : [],
    staff_ids: Array.isArray(filters?.staff_ids) ? filters.staff_ids : [],
    birthday_months: Array.isArray(filters?.birthday_months)
      ? filters.birthday_months
      : [],
  };
};

const toCurrencyInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? String(numberValue) : "";
};

const buildCampaignLink = (salonId, campaignId) => {
  if (!salonId || !campaignId || typeof window === "undefined") return "";
  const base = window.location.origin;
  const params = new URLSearchParams({
    utm_source: "sbin",
    utm_medium: "email",
    utm_campaign: String(campaignId),
  });
  return `${base}/portal/salon/${salonId}/book?${params.toString()}`;
};

const downloadCsv = (rows, filename) => {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    const values = headers.map((key) => {
      const raw = row[key];
      if (raw === null || raw === undefined) return "";
      const text = String(raw).replace(/\r?\n/g, " ").replace(/"/g, '""');
      return `"${text}"`;
    });
    lines.push(values.join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

function ShareToSocialPanel({ salonId, salonName, campaignId, template, socialLinks }) {
  const campaignLink = buildCampaignLink(salonId, campaignId);
  const subject = template?.subject || "New offer";
  const postText = `${salonName || "Our salon"}: ${subject}. Book here: ${campaignLink}`;

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Ignore clipboard errors.
    }
  };

  const links = [
    { label: "Facebook", url: socialLinks?.facebook_url },
    { label: "Instagram", url: socialLinks?.instagram_url },
    { label: "TikTok", url: socialLinks?.tiktok_url },
    { label: "YouTube", url: socialLinks?.youtube_url },
    { label: "Google Reviews", url: socialLinks?.google_review_url },
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">Share to Social</div>
        <div className="text-xs text-slate-500">
          Copy a post or open your salon social links.
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => handleCopy(postText)}
          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
        >
          Copy social post text
        </button>
        <button
          type="button"
          onClick={() => handleCopy(campaignLink)}
          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
        >
          Copy campaign link
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {links.map((link) =>
          link.url ? (
            <a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
            >
              {link.label}
            </a>
          ) : (
            <span
              key={link.label}
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-400"
            >
              {link.label}
            </span>
          )
        )}
      </div>
    </div>
  );
}

export default function Campaigns() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const salonTimeZone = user?.salon_timezone || "America/Toronto";

  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [socialLinks, setSocialLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [campaignFilters, setCampaignFilters] = useState({
    q: "",
    status: "all",
    goal: "",
    date_range: "all_time",
    include_archived: false,
  });

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [campaignId, setCampaignId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("send");
  const [sending, setSending] = useState(false);
  const [campaignAction, setCampaignAction] = useState(null);
  const [campaignActionSaving, setCampaignActionSaving] = useState(false);

  const [draft, setDraft] = useState({
    goal: "",
    template_id: "",
    name: "",
    promo_code: "",
    campaign_cost: "",
    schedule_mode: "now",
    scheduled_for: "",
    audience_filters_json: buildDefaultFilters(),
  });

  const loadAll = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const listParams = {
        limit: 100,
        q: campaignFilters.q.trim() || undefined,
        status: campaignFilters.status !== "all" ? campaignFilters.status : undefined,
        goal: campaignFilters.goal || undefined,
        date_range: campaignFilters.date_range || "all_time",
        include_archived:
          campaignFilters.include_archived || campaignFilters.status === "archived",
      };

      const [campaignRows, templateRows, serviceRows, categoryRows, staffRows, portalSettings] =
        await Promise.all([
          listMarketingCampaigns(salonId, listParams, token),
          listMarketingTemplates(salonId, token),
          listServices({ salon_id: salonId }, token),
          listCategories({ kind: "service" }, token),
          listStaff({ salon_id: salonId }, token),
          getPortalSettings(salonId, token).catch(() => null),
        ]);
      setCampaigns(Array.isArray(campaignRows) ? campaignRows : []);
      setTemplates(Array.isArray(templateRows) ? templateRows : []);
      setServices(Array.isArray(serviceRows) ? serviceRows : []);
      setCategories(Array.isArray(categoryRows) ? categoryRows : []);
      setStaff(Array.isArray(staffRows) ? staffRows : []);
      setSocialLinks(portalSettings?.portal_settings || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load campaigns.");
    } finally {
      setLoading(false);
    }
  }, [campaignFilters, salonId, token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const goalLabel = useMemo(() => {
    const match = CAMPAIGN_GOALS.find((goal) => goal.value === draft.goal);
    return match?.label || "";
  }, [draft.goal]);

  const filteredTemplates = useMemo(
    () =>
      templates.filter((template) =>
        draft.goal ? template.goal === draft.goal : true
      ),
    [templates, draft.goal]
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(draft.template_id)),
    [templates, draft.template_id]
  );

  const resetWizard = () => {
    setWizardOpen(false);
    setWizardStep(1);
    setCampaignId(null);
    setPreview(null);
    setDraft({
      goal: "",
      template_id: "",
      name: "",
      promo_code: "",
      campaign_cost: "",
      schedule_mode: "now",
      scheduled_for: "",
      audience_filters_json: buildDefaultFilters(),
    });
  };

  const handleContinueDraft = async (campaign) => {
    if (!campaign?.id) return;
    setError("");
    try {
      const latest = await getMarketingCampaign(campaign.id, token);
      if ((latest?.status || "").toLowerCase() !== "draft" || latest?.archived_at) {
        setError("Only active draft campaigns can be continued.");
        await loadAll();
        return;
      }
      setCampaignId(latest.id);
      setPreview(null);
      setConfirmOpen(false);
      setConfirmMode("send");
      setDraft({
        goal: latest.goal || "",
        template_id: latest.template_id ? String(latest.template_id) : "",
        name: latest.name || "",
        promo_code: latest.promo_code || "",
        campaign_cost: toCurrencyInputValue(latest.campaign_cost),
        schedule_mode: latest.scheduled_for ? "schedule" : "now",
        scheduled_for: "",
        audience_filters_json: normalizeAudienceFilters(latest.audience_filters_json),
      });
      setWizardStep(latest.goal && latest.template_id ? 3 : latest.goal ? 2 : 1);
      setWizardOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to open campaign draft.");
    }
  };

  const createDraftCampaign = async () => {
    if (!salonId || !draft.goal) return null;
    const payload = {
      goal: draft.goal,
      template_id: draft.template_id ? Number(draft.template_id) : null,
      name: draft.name || null,
    };
    const created = await createMarketingCampaign(salonId, payload, token);
    setCampaignId(created?.id || null);
    return created?.id || null;
  };

  const updateDraftCampaign = async (payload, targetCampaignId = campaignId) => {
    if (!targetCampaignId) return null;
    return updateMarketingCampaign(targetCampaignId, payload, token);
  };

  const handleNextFromTemplate = async () => {
    if (!draft.goal || !draft.template_id) {
      setError("Choose a goal and template to continue.");
      return null;
    }
    setError("");
    let id = campaignId;
    try {
      if (!id) {
        id = await createDraftCampaign();
      } else {
        await updateDraftCampaign({
          goal: draft.goal,
          template_id: Number(draft.template_id),
          name: draft.name || null,
        });
      }
      if (id) {
        setWizardStep(3);
        return id;
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save campaign draft.");
    }
  };

  const handlePreview = async (targetCampaignId = campaignId) => {
    if (!targetCampaignId) return null;
    setError("");
    try {
      await updateDraftCampaign({
        audience_filters_json: draft.audience_filters_json,
        promo_code: draft.promo_code || null,
        campaign_cost: draft.campaign_cost ? Number(draft.campaign_cost) : null,
      }, targetCampaignId);
      const previewData = await previewCampaignRecipients(targetCampaignId, token);
      setPreview(previewData || null);
      setWizardStep(4);
      return previewData || null;
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to preview recipients.");
    }
  };

  const canOpenWizardStep = (step) => {
    if (step <= 1) return true;
    if (step === 2) return Boolean(draft.goal);
    if (step === 3) return Boolean(draft.goal && draft.template_id);
    if (step === 4) return Boolean(campaignId || (draft.goal && draft.template_id));
    return Boolean(preview);
  };

  const handleWizardStepClick = async (step) => {
    if (step === wizardStep) return;
    setError("");

    if (step === 1) {
      setWizardStep(1);
      return;
    }

    if (step === 2) {
      if (!draft.goal) {
        setError("Choose a goal before opening the template step.");
        return;
      }
      setWizardStep(2);
      return;
    }

    if (!draft.goal || !draft.template_id) {
      setError("Choose a goal and template before opening later steps.");
      return;
    }

    let id = campaignId;
    if (!id || wizardStep <= 2) {
      id = await handleNextFromTemplate();
      if (!id) return;
    }

    if (step === 3) {
      setWizardStep(3);
      return;
    }

    let previewData = preview;
    if (!previewData) {
      previewData = await handlePreview(id);
      if (!previewData) return;
    }

    setWizardStep(step);
  };

  const handleSendNow = async () => {
    if (!campaignId) return;
    setSending(true);
    setError("");
    try {
      await updateDraftCampaign({
        audience_filters_json: draft.audience_filters_json,
        promo_code: draft.promo_code || null,
        campaign_cost: draft.campaign_cost ? Number(draft.campaign_cost) : null,
      });
      await sendCampaignNow(campaignId, token);
      await loadAll();
      resetWizard();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to send campaign.");
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!campaignId) return;
    const scheduledUtc = parseLocalDateTimeInTimeZone(
      draft.scheduled_for,
      salonTimeZone
    );
    if (!scheduledUtc) {
      setError("Choose a schedule time to continue.");
      return;
    }
    setSending(true);
    setError("");
    try {
      await updateDraftCampaign({
        scheduled_for: scheduledUtc.toISOString(),
        audience_filters_json: draft.audience_filters_json,
        promo_code: draft.promo_code || null,
        campaign_cost: draft.campaign_cost ? Number(draft.campaign_cost) : null,
      });
      await loadAll();
      resetWizard();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to schedule campaign.");
    } finally {
      setSending(false);
    }
  };

  const handleCampaignFilterChange = (key, value) => {
    setCampaignFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearCampaignFilters = () => {
    setCampaignFilters({
      q: "",
      status: "all",
      goal: "",
      date_range: "all_time",
      include_archived: false,
    });
  };

  const openCampaignAction = (type, campaign) => {
    setCampaignAction({ type, campaign });
  };

  const closeCampaignAction = () => {
    if (campaignActionSaving) return;
    setCampaignAction(null);
  };

  const handleConfirmCampaignAction = async () => {
    if (!campaignAction?.campaign?.id) return;
    setCampaignActionSaving(true);
    setError("");
    try {
      if (campaignAction.type === "delete") {
        await deleteMarketingCampaign(campaignAction.campaign.id, token);
      } else if (campaignAction.type === "cancel") {
        await cancelCampaign(campaignAction.campaign.id, token);
      } else if (campaignAction.type === "archive") {
        await archiveMarketingCampaign(campaignAction.campaign.id, token);
      } else if (campaignAction.type === "restore") {
        await restoreMarketingCampaign(campaignAction.campaign.id, token);
      }
      setCampaignAction(null);
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update campaign.");
    } finally {
      setCampaignActionSaving(false);
    }
  };

  const campaignActionCopy = useMemo(() => {
    const type = campaignAction?.type;
    const name = campaignAction?.campaign?.name || "this campaign";
    if (type === "delete") {
      return {
        title: "Delete draft campaign?",
        message: `Delete "${name}"?`,
        subMessage: "Draft campaigns with no send data can be deleted. This cannot be undone.",
        confirmLabel: "Delete Draft",
        confirmingLabel: "Deleting...",
      };
    }
    if (type === "restore") {
      return {
        title: "Restore campaign?",
        message: `Restore "${name}" to the active campaign list?`,
        subMessage: "The campaign history and metrics will stay intact.",
        confirmLabel: "Restore",
        confirmingLabel: "Restoring...",
      };
    }
    if (type === "cancel") {
      return {
        title: "Cancel scheduled campaign?",
        message: `Cancel the scheduled send for "${name}"?`,
        subMessage:
          "No emails will be sent. The campaign will stay in your list as cancelled.",
        confirmLabel: "Cancel Schedule",
        confirmingLabel: "Cancelling...",
      };
    }
    return {
      title: "Archive campaign?",
      message: `Archive "${name}"?`,
      subMessage:
        "Archived campaigns are hidden from the default list but can still be viewed with the Archived filter.",
      confirmLabel: "Archive",
      confirmingLabel: "Archiving...",
    };
  }, [campaignAction]);

  const renderStatusPill = (status, archivedAt = null) => {

    if (archivedAt) {

      return (

        <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700">

          archived

        </span>

      );

    }
    const normalized = (status || "").toLowerCase();
    const colorMap = {
      draft: "bg-slate-100 text-slate-700",
      scheduled: "bg-blue-100 text-blue-700",
      sending: "bg-amber-100 text-amber-700",
      completed: "bg-emerald-100 text-emerald-700",
      cancelled: "bg-rose-100 text-rose-700",
    };
    return (
      <span
        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
          colorMap[normalized] || "bg-slate-100 text-slate-700"
        }`}
      >
        {status || "draft"}
      </span>
    );
  };

  const handleMultiSelect = (event, key) => {
    const values = Array.from(event.target.selectedOptions).map((option) =>
      Number(option.value)
    );
    setDraft((prev) => ({
      ...prev,
      audience_filters_json: {
        ...prev.audience_filters_json,
        [key]: values.filter((val) => Number.isFinite(val)),
      },
    }));
  };

  const toggleBirthdayMonth = (month) => {
    setDraft((prev) => {
      const current = prev.audience_filters_json.birthday_months || [];
      const exists = current.includes(month);
      const nextMonths = exists
        ? current.filter((value) => value !== month)
        : [...current, month];
      return {
        ...prev,
        audience_filters_json: {
          ...prev.audience_filters_json,
          birthday_months: nextMonths,
        },
      };
    });
  };

  const previewCsvRows = useMemo(() => {
    if (!preview?.recipients) return [];
    return preview.recipients.map((recipient) => ({
      name: recipient.full_name || "",
      email: recipient.email || "",
      last_visit: recipient.last_visit_date || "",
      lifetime_spend: recipient.lifetime_spend || 0,
    }));
  }, [preview]);

  const scheduledLabel = useMemo(() => {
    if (!draft.scheduled_for) return "";
    const scheduledUtc = parseLocalDateTimeInTimeZone(
      draft.scheduled_for,
      salonTimeZone
    );
    if (!scheduledUtc) return draft.scheduled_for;
    return formatDateTimeInTimeZone(scheduledUtc.toISOString(), salonTimeZone);
  }, [draft.scheduled_for, salonTimeZone]);

  const confirmMessage = useMemo(() => {
    if (confirmMode === "schedule") {
      return `Schedule this campaign for ${scheduledLabel || "the selected time"}?`;
    }
    return `Send this campaign now to ${preview?.total_count || 0} recipients?`;
  }, [confirmMode, preview, scheduledLabel]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 pr-20 flex items-center justify-between relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Campaigns
            </div>
            <div className="mr-8 flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/manager/marketing/campaigns/templates")}
                className="rounded-full border border-white/70 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-white/10"
              >
                Templates
              </button>
              <button
                type="button"
                onClick={() => {
                  setWizardOpen(true);
                  setWizardStep(1);
                  setCampaignId(null);
                  setPreview(null);
                  setDraft({
                    goal: "",
                    template_id: "",
                    name: "",
                    promo_code: "",
                    campaign_cost: "",
                    schedule_mode: "now",
                    scheduled_for: "",
                    audience_filters_json: buildDefaultFilters(),
                  });
                }}
                className="rounded-full bg-white px-4 py-2 text-xs md:text-sm font-semibold text-rose-600 hover:bg-rose-50"
              >
                New Campaign
              </button>
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <ReportHelpCard
              title="Help Guide"
              subtitle="Create, send, and track client campaigns."
              sections={CAMPAIGN_HELP_SECTIONS}
              storageKey="sbin_campaigns_help"
              showLabel="Show Help"
              hideLabel="Hide Help"
            />

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Campaigns
                  </div>
                  <div className="text-xs md:text-sm text-slate-500">
                    Salon: {salonName || "Unknown"} (ID {salonId || "--"})
                  </div>
                </div>
                {loading && <div className="text-xs text-slate-500">Loading...</div>}
              </div>
              <div className="mt-4 grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[minmax(220px,1.4fr)_minmax(140px,0.8fr)_minmax(180px,1fr)_minmax(140px,0.8fr)_auto] md:items-end">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Search
                  <input
                    type="search"
                    value={campaignFilters.q}
                    onChange={(event) =>
                      handleCampaignFilterChange("q", event.target.value)
                    }
                    placeholder="Campaign name or promo code"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-200"
                  />
                </label>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                  <select
                    value={campaignFilters.status}
                    onChange={(event) =>
                      handleCampaignFilterChange("status", event.target.value)
                    }
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-200"
                  >
                    {CAMPAIGN_STATUS_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Goal
                  <select
                    value={campaignFilters.goal}
                    onChange={(event) =>
                      handleCampaignFilterChange("goal", event.target.value)
                    }
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-200"
                  >
                    <option value="">All goals</option>
                    {CAMPAIGN_GOALS.map((goal) => (
                      <option key={goal.value} value={goal.value}>
                        {goal.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Date
                  <select
                    value={campaignFilters.date_range}
                    onChange={(event) =>
                      handleCampaignFilterChange("date_range", event.target.value)
                    }
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-700 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-200"
                  >
                    {CAMPAIGN_DATE_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={campaignFilters.include_archived}
                      onChange={(event) =>
                        handleCampaignFilterChange(
                          "include_archived",
                          event.target.checked
                        )
                      }
                      className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-300"
                    />
                    Show archived
                  </label>
                  <button
                    type="button"
                    onClick={clearCampaignFilters}
                    className="rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <details className="mt-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <summary className="cursor-pointer text-sm font-semibold text-slate-800">
                  Campaign status guide
                </summary>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {CAMPAIGN_STATUS_GUIDE.map((item) => (
                    <div
                      key={item.status}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                    >
                      <div className="text-sm font-semibold text-slate-900">
                        {item.status}
                      </div>
                      <div className="mt-1 text-xs leading-5 text-slate-600">
                        {item.description}
                      </div>
                    </div>
                  ))}
                </div>
              </details>

              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Campaign</th>
                      <th className="px-3 py-2 text-left">Goal</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Sent</th>
                      <th className="px-3 py-2 text-left">
                        <InfoTooltip text={CAMPAIGN_TOOLTIPS.tracking} label="Open rate" />
                      </th>
                      <th className="px-3 py-2 text-left">
                        <InfoTooltip text={CAMPAIGN_TOOLTIPS.tracking} label="Click rate" />
                      </th>
                      <th className="px-3 py-2 text-left">Revenue</th>
                      <th className="px-3 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id}>
                        <td className="px-3 py-2 font-semibold text-slate-900">
                          {campaign.name}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {CAMPAIGN_GOALS.find((g) => g.value === campaign.goal)?.label ||
                            campaign.goal}
                        </td>
                        <td className="px-3 py-2">
                          {renderStatusPill(campaign.status, campaign.archived_at)}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {campaign.send_started_at
                            ? formatDateTimeInTimeZone(
                                campaign.send_started_at,
                                salonTimeZone
                              )
                            : campaign.scheduled_for
                            ? `Scheduled ${formatDateTimeInTimeZone(
                                campaign.scheduled_for,
                                salonTimeZone
                              )}`
                            : "--"}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {pct(campaign.open_rate)}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {pct(campaign.click_rate)}
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {currency.format(Number(campaign.total_revenue || 0))}
                        </td>
                        <td className="px-3 py-2">
                          {(campaign.status || "").toLowerCase() === "draft" &&
                          !campaign.archived_at ? (
                            <button
                              type="button"
                              onClick={() => handleContinueDraft(campaign)}
                              className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
                            >
                              Continue
                            </button>
                          ) : (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/manager/marketing/campaigns/${campaign.id}`)
                            }
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            View
                          </button>
                          )}
                          {campaign.archived_at ? (
                            <button
                              type="button"
                              onClick={() => openCampaignAction("restore", campaign)}
                              className="ml-2 rounded-full border border-emerald-300 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                            >
                              Restore
                            </button>
                          ) : (campaign.status || "").toLowerCase() === "draft" ? (
                            <button
                              type="button"
                              onClick={() => openCampaignAction("delete", campaign)}
                              className="ml-2 rounded-full border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                            >
                              Delete draft
                            </button>
                          ) : (campaign.status || "").toLowerCase() === "scheduled" ? (
                            <button
                              type="button"
                              onClick={() => openCampaignAction("cancel", campaign)}
                              className="ml-2 rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50"
                            >
                              Cancel schedule
                            </button>
                          ) : !["scheduled", "sending"].includes(
                              (campaign.status || "").toLowerCase()
                            ) ? (
                            <button
                              type="button"
                              onClick={() => openCampaignAction("archive", campaign)}
                              className="ml-2 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Archive
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                    {campaigns.length === 0 && !loading && (
                      <tr>
                        <td
                          colSpan="8"
                          className="px-3 py-6 text-center text-sm text-slate-500"
                        >
                          No campaigns yet. Start a new campaign to reach clients.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {wizardOpen && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-base md:text-lg font-semibold text-slate-900">
                      {campaignId ? "Continue campaign draft" : "New campaign"}
                    </div>
                    <div className="text-xs text-slate-500">
                      Wizard step {wizardStep} of 5. Drafts are not active until you send or
                      schedule them.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetWizard}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  {CAMPAIGN_WIZARD_STEPS.map((label, idx) => {
                    const stepNumber = idx + 1;
                    const isCurrent = wizardStep === stepNumber;
                    const disabled = !canOpenWizardStep(stepNumber);
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => handleWizardStepClick(stepNumber)}
                        disabled={disabled}
                        aria-current={isCurrent ? "step" : undefined}
                        className={`rounded-full px-3 py-1 font-semibold transition ${
                          isCurrent
                            ? "bg-rose-500 text-white"
                            : disabled
                            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        {stepNumber}. {label}
                      </button>
                    );
                  })}
                </div>

                {wizardStep === 1 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <span>Choose a marketing goal</span>
                      <InfoTooltip text={CAMPAIGN_TOOLTIPS.goal} label="Goal" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {CAMPAIGN_GOALS.map((goal) => (
                        <button
                          key={goal.value}
                          type="button"
                          onClick={() =>
                            setDraft((prev) => ({
                              ...prev,
                              goal: goal.value,
                              template_id: "",
                            }))
                          }
                          className={`rounded-2xl border p-4 text-left transition ${
                            draft.goal === goal.value
                              ? "border-rose-500 bg-rose-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="text-sm font-semibold text-slate-900">
                            {goal.label}
                          </div>
                          <div className="text-xs text-slate-500">
                            Align templates and KPIs to this goal.
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-500">
                        Goals help you pick the right message and measure success.
                      </div>
                      <button
                        type="button"
                        onClick={() => setWizardStep(2)}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                        disabled={!draft.goal}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}

                {wizardStep === 2 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="text-sm font-semibold text-slate-700">
                          Template selection
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500">
                            Campaign name (optional)
                          </label>
                          <input
                            type="text"
                            value={draft.name}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                            placeholder={goalLabel ? `${goalLabel} - Today` : "Campaign name"}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500">
                            Template
                          </label>
                          <select
                            value={draft.template_id}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                template_id: e.target.value,
                              }))
                            }
                            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          >
                            <option value="">Select template</option>
                            {filteredTemplates.map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                          {filteredTemplates.length === 0 && (
                            <div className="mt-2 text-xs text-slate-500">
                              No templates for this goal yet. Create one in the template
                              library.
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            navigate("/manager/marketing/campaigns/templates")
                          }
                          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Manage templates
                        </button>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Preview
                        </div>
                        {selectedTemplate ? (
                          <div className="space-y-2 text-sm text-slate-700">
                            <div>
                              <span className="font-semibold text-slate-900">Subject:</span>{" "}
                              {selectedTemplate.subject}
                            </div>
                            <div className="text-xs text-slate-500">
                              {selectedTemplate.preheader || "No preheader"}
                            </div>
                            <SafeHtml
                              className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600"
                               html={selectedTemplate.body_html} />
                          </div>
                        ) : (
                          <div className="text-sm text-slate-500">
                            Select a template to preview.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setWizardStep(1)}
                        className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleNextFromTemplate}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                        disabled={!draft.goal || !draft.template_id}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}

                {wizardStep === 3 && (
                  <div className="space-y-4">
                    <div className="text-sm font-semibold text-slate-700">
                      Audience filters
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase">
                          <InfoTooltip text={CAMPAIGN_TOOLTIPS.recency} label="Recency" />
                        </div>
                        <select
                          value={draft.audience_filters_json.recency.mode}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              audience_filters_json: {
                                ...prev.audience_filters_json,
                                recency: {
                                  ...prev.audience_filters_json.recency,
                                  mode: e.target.value,
                                },
                              },
                            }))
                          }
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        >
                          <option value="within">Visited within</option>
                          <option value="not_within">Not visited in</option>
                        </select>
                        <input
                          type="number"
                          min="1"
                          value={draft.audience_filters_json.recency.days}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              audience_filters_json: {
                                ...prev.audience_filters_json,
                                recency: {
                                  ...prev.audience_filters_json.recency,
                                  days: e.target.value,
                                },
                              },
                            }))
                          }
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        />
                        <span className="text-xs text-slate-500">days</span>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase">
                          <InfoTooltip text={CAMPAIGN_TOOLTIPS.frequency} label="Frequency" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="1"
                            value={draft.audience_filters_json.frequency.days}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                audience_filters_json: {
                                  ...prev.audience_filters_json,
                                  frequency: {
                                    ...prev.audience_filters_json.frequency,
                                    days: e.target.value,
                                  },
                                },
                              }))
                            }
                            className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          />
                          <input
                            type="number"
                            min="1"
                            value={draft.audience_filters_json.frequency.min}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                audience_filters_json: {
                                  ...prev.audience_filters_json,
                                  frequency: {
                                    ...prev.audience_filters_json.frequency,
                                    min: e.target.value,
                                  },
                                },
                              }))
                            }
                            className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          />
                        </div>
                        <div className="text-xs text-slate-500">
                          Visits in last X days greater than or equal to N
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase">
                          <InfoTooltip text={CAMPAIGN_TOOLTIPS.revenue} label="Revenue" />
                        </div>
                        <input
                          type="number"
                          min="0"
                          placeholder="Lifetime spend min"
                          value={draft.audience_filters_json.lifetime_spend_min}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              audience_filters_json: {
                                ...prev.audience_filters_json,
                                lifetime_spend_min: e.target.value,
                              },
                            }))
                          }
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            min="1"
                            placeholder="Days"
                            value={draft.audience_filters_json.recent_spend_days}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                audience_filters_json: {
                                  ...prev.audience_filters_json,
                                  recent_spend_days: e.target.value,
                                },
                              }))
                            }
                            className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          />
                          <input
                            type="number"
                            min="0"
                            placeholder="Spend min"
                            value={draft.audience_filters_json.recent_spend_min}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                audience_filters_json: {
                                  ...prev.audience_filters_json,
                                  recent_spend_min: e.target.value,
                                },
                              }))
                            }
                            className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="text-xs font-semibold uppercase text-slate-600">
                          Services
                        </div>
                        <select
                          multiple
                          value={draft.audience_filters_json.service_ids.map(String)}
                          onChange={(e) => handleMultiSelect(e, "service_ids")}
                          className="h-36 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        >
                          {services.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.name}
                            </option>
                          ))}
                        </select>
                        <div className="text-xs text-slate-500">
                          Select one or more services to target.
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="text-xs font-semibold uppercase text-slate-600">
                          Service categories
                        </div>
                        <select
                          multiple
                          value={draft.audience_filters_json.service_category_ids.map(String)}
                          onChange={(e) => handleMultiSelect(e, "service_category_ids")}
                          className="h-36 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        >
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        <div className="text-xs text-slate-500">
                          Use categories to broaden the audience.
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="text-xs font-semibold uppercase text-slate-600">
                          Technician
                        </div>
                        <select
                          multiple
                          value={draft.audience_filters_json.staff_ids.map(String)}
                          onChange={(e) => handleMultiSelect(e, "staff_ids")}
                          className="h-36 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        >
                          {staff.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.display_name || member.full_name || member.name}
                            </option>
                          ))}
                        </select>
                        <div className="text-xs text-slate-500">
                          Target clients who saw specific technicians.
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                      <div className="text-xs font-semibold uppercase text-slate-600">
                        Birthday month (optional)
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {MONTHS.map((month) => (
                          <button
                            key={month.value}
                            type="button"
                            onClick={() => toggleBirthdayMonth(month.value)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                              draft.audience_filters_json.birthday_months.includes(
                                month.value
                              )
                                ? "bg-rose-500 text-white border-rose-500"
                                : "border-slate-200 text-slate-600"
                            }`}
                          >
                            {month.label}
                          </button>
                        ))}
                      </div>
                      <div className="text-xs text-slate-500">
                        If no birthdays are on file, this filter is ignored.
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 p-4 space-y-2">
                      <div className="text-xs font-semibold uppercase text-slate-600">
                        Audience rules
                      </div>
                      <div className="text-sm text-slate-600">
                        Only clients with an email address are eligible. Unsubscribed
                        clients are always excluded.
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setWizardStep(2)}
                        className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handlePreview}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                        disabled={!campaignId}
                      >
                        Preview recipients
                      </button>
                    </div>
                  </div>
                )}

                {wizardStep === 4 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2 space-y-3">
                        <div className="rounded-2xl border border-slate-200 p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">
                                Preview recipients
                              </div>
                              <div className="text-xs text-slate-500">
                                Total eligible clients: {preview?.total_count || 0}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                downloadCsv(previewCsvRows, "campaign_recipients_preview.csv")
                              }
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Export CSV
                            </button>
                          </div>
                          <div className="mt-3 overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                  <th className="px-3 py-2 text-left">Name</th>
                                  <th className="px-3 py-2 text-left">Email</th>
                                  <th className="px-3 py-2 text-left">Last visit</th>
                                  <th className="px-3 py-2 text-left">Lifetime spend</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {(preview?.recipients || []).map((recipient, index) => (
                                  <tr key={`${recipient.client_id || index}`}>
                                    <td className="px-3 py-2 text-slate-700">
                                      {recipient.full_name || "--"}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">
                                      {recipient.email || "--"}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">
                                      {recipient.last_visit_date || "--"}
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">
                                      {currency.format(Number(recipient.lifetime_spend || 0))}
                                    </td>
                                  </tr>
                                ))}
                                {(preview?.recipients || []).length === 0 && (
                                  <tr>
                                    <td
                                      colSpan="4"
                                      className="px-3 py-4 text-center text-xs text-slate-500"
                                    >
                                      No recipients found. Adjust filters and try again.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Email preview
                          </div>
                          {selectedTemplate ? (
                            <div className="space-y-2 text-sm text-slate-700">
                              <div>
                                <span className="font-semibold text-slate-900">Subject:</span>{" "}
                                {selectedTemplate.subject}
                              </div>
                              <div className="text-xs text-slate-500">
                                {selectedTemplate.preheader || "No preheader"}
                              </div>
                              <SafeHtml
                                className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600"
                                 html={selectedTemplate.body_html} />
                              <div className="text-xs text-slate-500">
                                Attribution window: {selectedTemplate.attribution_window_days} days
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-slate-500">
                              Select a template to preview.
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-4">
                        <ShareToSocialPanel
                          salonId={salonId}
                          salonName={salonName}
                          campaignId={campaignId}
                          template={selectedTemplate}
                          socialLinks={socialLinks}
                        />
                        <div className="rounded-2xl border border-slate-200 p-4 space-y-2">
                          <div className="text-xs font-semibold uppercase text-slate-600">
                            Audience snapshot
                          </div>
                          <div className="text-sm text-slate-700">
                            Goal: {goalLabel || "--"}
                          </div>
                          <div className="text-sm text-slate-700">
                            Template: {selectedTemplate?.name || "--"}
                          </div>
                          <div className="text-sm text-slate-700">
                            Recipients: {preview?.total_count || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setWizardStep(3)}
                        className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setWizardStep(5)}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                        disabled={!preview}
                      >
                        Continue to send
                      </button>
                    </div>
                  </div>
                )}

                {wizardStep === 5 && (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                      <div className="text-sm font-semibold text-slate-900">Send options</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="schedule_mode"
                            value="now"
                            checked={draft.schedule_mode === "now"}
                            onChange={() =>
                              setDraft((prev) => ({ ...prev, schedule_mode: "now" }))
                            }
                          />
                          Send now
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="schedule_mode"
                            value="schedule"
                            checked={draft.schedule_mode === "schedule"}
                            onChange={() =>
                              setDraft((prev) => ({ ...prev, schedule_mode: "schedule" }))
                            }
                          />
                          Schedule for later
                        </label>
                      </div>
                      {draft.schedule_mode === "schedule" && (
                        <div>
                          <label className="text-xs font-semibold text-slate-500">
                            Schedule time (salon time zone: {salonTimeZone})
                          </label>
                          <input
                            type="datetime-local"
                            value={draft.scheduled_for}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                scheduled_for: e.target.value,
                              }))
                            }
                            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="text-xs font-semibold uppercase text-slate-600">
                          Campaign details
                        </div>
                        <div className="text-sm text-slate-700">
                          Goal: {goalLabel || "--"}
                        </div>
                        <div className="text-sm text-slate-700">
                          Template: {selectedTemplate?.name || "--"}
                        </div>
                        <div className="text-sm text-slate-700">
                          Recipients: {preview?.total_count || 0}
                        </div>
                        <div className="text-sm text-slate-700">
                          Attribution window: {selectedTemplate?.attribution_window_days || 14} days
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                        <div className="text-xs font-semibold uppercase text-slate-600">
                          Promo and cost
                        </div>
                        <input
                          type="text"
                          placeholder="Promo code (optional)"
                          value={draft.promo_code}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              promo_code: e.target.value,
                            }))
                          }
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Campaign cost (optional)"
                          value={draft.campaign_cost}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              campaign_cost: e.target.value,
                            }))
                          }
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                        />
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <InfoTooltip
                            text={CAMPAIGN_TOOLTIPS.unsubscribe}
                            label="Unsubscribe"
                          />
                          Unsubscribe link is included automatically.
                        </div>
                      </div>
                      <ShareToSocialPanel
                        salonId={salonId}
                        salonName={salonName}
                        campaignId={campaignId}
                        template={selectedTemplate}
                        socialLinks={socialLinks}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setWizardStep(4)}
                        className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700"
                      >
                        Back
                      </button>
                      {draft.schedule_mode === "schedule" ? (
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmMode("schedule");
                            setConfirmOpen(true);
                          }}
                          className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                          disabled={!draft.scheduled_for || sending}
                        >
                          Schedule campaign
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmMode("send");
                            setConfirmOpen(true);
                          }}
                          className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white"
                          disabled={sending}
                        >
                          Send now
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={Boolean(campaignAction)}
        onClose={closeCampaignAction}
        onCancel={closeCampaignAction}
        onConfirm={handleConfirmCampaignAction}
        title={campaignActionCopy.title}
        message={campaignActionCopy.message}
        subMessage={campaignActionCopy.subMessage}
        confirmLabel={campaignActionCopy.confirmLabel}
        confirmingLabel={campaignActionCopy.confirmingLabel}
        isSubmitting={campaignActionSaving}
      />

      <ConfirmModal

        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          if (confirmMode === "schedule") {
            handleSchedule();
          } else {
            handleSendNow();
          }
        }}
        title={confirmMode === "schedule" ? "Schedule campaign" : "Send campaign"}
        message={confirmMessage}
        confirmingLabel={confirmMode === "schedule" ? "Scheduling..." : "Sending..."}
        isSubmitting={sending}
      />
    </div>
  );
}
